/** localStorage helpers for nurse attendance drafts (session+date+class). */

const CLASS_KEY = 'syms_attendance_last_class';
const TOUCH_KEY = 'syms_attendance_touch_mode';
const DRAFT_PREFIX = 'syms_attendance_draft_';

export function draftStorageKey(sessionId, date, classKey) {
  return `${DRAFT_PREFIX}${sessionId || 'none'}_${date || ''}_${classKey || 'all'}`;
}

export function readLastClassFilter() {
  try {
    const v = localStorage.getItem(CLASS_KEY);
    return v == null ? '' : v;
  } catch {
    return '';
  }
}

export function writeLastClassFilter(classKey) {
  try {
    localStorage.setItem(CLASS_KEY, classKey == null ? '' : String(classKey));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Explicit touch-mode preference for Surface / tablet nurses.
 * Returns null when unset (caller should default from `(any-pointer: coarse)`).
 * Do not key cards off CSS width — Surface Pro is ≥900px in both orientations.
 */
export function readTouchModePreference() {
  try {
    const v = localStorage.getItem(TOUCH_KEY);
    if (v === '1' || v === 'true') return true;
    if (v === '0' || v === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

export function writeTouchModePreference(enabled) {
  try {
    localStorage.setItem(TOUCH_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function readAttendanceDraft(sessionId, date, classKey) {
  try {
    const raw = localStorage.getItem(draftStorageKey(sessionId, date, classKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      local: parsed.local && typeof parsed.local === 'object' ? parsed.local : {},
      dirty: parsed.dirty && typeof parsed.dirty === 'object' ? parsed.dirty : {},
    };
  } catch {
    return null;
  }
}

export function writeAttendanceDraft(sessionId, date, classKey, local, dirty) {
  try {
    const key = draftStorageKey(sessionId, date, classKey);
    if (!dirty || Object.keys(dirty).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify({ local, dirty }));
  } catch {
    /* ignore */
  }
}

export function clearAttendanceDraft(sessionId, date, classKey) {
  try {
    localStorage.removeItem(draftStorageKey(sessionId, date, classKey));
  } catch {
    /* ignore */
  }
}

/** Map a stored AttendanceRecord to teacher-style UI status. */
export function recordToUiStatus(rec) {
  if (!rec || !rec.status) return '';
  if (rec.status === 'absent') return 'absent';
  if (rec.status === 'present') {
    if (rec.late_arrival_time) return 'late';
    if (rec.early_dismissal_time) return 'early';
    return 'present';
  }
  return '';
}

export const STATUS_ORDER = ['', 'present', 'absent', 'late', 'early'];

export function cycleStatus(current) {
  const i = STATUS_ORDER.indexOf(current);
  const next = i < 0 ? 1 : (i + 1) % STATUS_ORDER.length;
  return STATUS_ORDER[next];
}

export function statusLabel(s) {
  switch (s) {
    case 'present':
      return 'Present';
    case 'absent':
      return 'Absent';
    case 'late':
      return 'Late';
    case 'early':
      return 'Early';
    default:
      return '—';
  }
}

export function statusColor(s) {
  switch (s) {
    case 'present':
      return 'success';
    case 'absent':
      return 'error';
    case 'late':
      return 'warning';
    case 'early':
      return 'info';
    default:
      return 'default';
  }
}
